import React from "react"

type DescriptionProps = {
  children: React.ReactNode
  className?: string
}

// Regex to match [text](url) markdown links
const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+|www\.[^\s)]+)\)/gi
// Regex to match URLs (http, https, www)
const urlRegex = /(\bhttps?:\/\/[^\s<]+|\bwww\.[^\s<]+)/gi

export function CustomText({ children, className }: DescriptionProps) {
  if (typeof children !== "string") {
    return <p className={className}>{children}</p>
  }
  
  // Parse the description for markdown links and plain URLs
  const parts = React.useMemo(() => {
    const result: (string | { url: string; text: string })[] = []
    let lastIndex = 0
    let match: RegExpExecArray | null

    // First, find all markdown links
    while ((match = markdownLinkRegex.exec(children)) !== null) {
      if (match.index > lastIndex) {
        // Push text before the markdown link
        result.push(children.slice(lastIndex, match.index))
      }
      let url = match[2]
      // Add protocol if missing
      if (!/^https?:\/\//i.test(url)) {
        url = "https://" + url
      }
      result.push({ url, text: match[1] })
      lastIndex = match.index + match[0].length
    }

    // Handle the rest of the string after the last markdown link
    let remaining = children.slice(lastIndex)
    if (remaining) {
      // Now, parse plain URLs in the remaining text
      let urlLastIndex = 0
      let urlMatch: RegExpExecArray | null
      while ((urlMatch = urlRegex.exec(remaining)) !== null) {
        if (urlMatch.index > urlLastIndex) {
          result.push(remaining.slice(urlLastIndex, urlMatch.index))
        }
        let url = urlMatch[0]
        if (!/^https?:\/\//i.test(url)) {
          url = "https://" + url
        }
        result.push({ url, text: urlMatch[0].replace(/^https?:\/\//, "") })
        urlLastIndex = urlMatch.index + urlMatch[0].length
      }
      if (urlLastIndex < remaining.length) {
        result.push(remaining.slice(urlLastIndex))
      }
    }

    return result
  }, [children])

  return (
    <p className={className}>
      {parts.map((part, i) =>
        typeof part === "string" ? (
          <React.Fragment key={i}>{part}</React.Fragment>
        ) : (
          <a
            key={i}
            href={part.url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline break-all"
            onClick={e => {
                // Prevent the click from bubbling up to parent elements (like a card link)
                e.stopPropagation();
            }}
            onMouseDown={e => {
                // Prevent focus/active bubbling to parent
                e.stopPropagation();
            }}
          >
            {part.text}
          </a>
        )
      )}
    </p>
  )
}
